import { Feature, Map } from 'ol';
import { Geometry } from 'ol/geom';
import {
  Switch,
  SwitchChangeEvent,
  RadioButtonChangeEvent
} from '@progress/kendo-react-inputs';
import cloneDeep from 'lodash.clonedeep';
import Popup from 'ol-ext/overlay/Popup';
import { createPortal } from 'react-dom';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import GeoImage from 'ol-ext/source/GeoImage';
import { fromLonLat, toLonLat } from 'ol/proj';
import { useLocalStorage, useToggle } from 'usehooks-ts';
import { PanelBar, PanelBarItem } from '@progress/kendo-react-layout';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import React, { useCallback, useContext, useEffect, useState } from 'react';

import {
  AddResourceAsync,
  DeleteResourceAsync,
  GetResourceById,
  GetResourceEntity,
  GetResourceKeys,
  GetResourceValue,
  UpdateGameDocState,
  UpdateMapAsync,
  UpdateResourceAsync
} from '../../utils/game-document';
import {
  AreaEntity,
  EntityEditor,
  MapEntity,
  ResourceEntity,
  TaskEntity,
  ZoneEntity
} from '../../types/game-document/';
import MapSettings from './map-settings';
import { MapContents } from './map-contents';
import { Coordinate } from '../../types/place';
import {
  GetZoneById,
  GetAreaById,
  GetTaskById,
  GetTasksByIds,
  CopyTaskAsync,
  DeleteZoneAsync,
  DeleteAreaAsync,
  UpdateAreaAsync,
  UpdateTaskAsync,
  GetMapByIdAsync,
  UpdateZoneAsync,
  GetAreasByZoneId,
  GetAreasWorldMap,
  GetTasksByZoneId,
  UpdateRoutesAsync,
  RemoveRoutesAsync,
  GetDetailRoutesById,
  GetAssetRoutesByMapId,
  SaveEntityEditorAsync,
  GetDetailRoutesByMapId,
  RemoveTaskBoundaryAsync,
  GetDetailRoutesByRouteId
} from '../../utils/game-document/assets/index';
import { MapPopupMenu } from './map-popup-menu';
import { uuid } from '../../types/common-helper';
import { MapBehaviours } from './map-behaviours';
import { Algorithm } from '../../types/algorithm';
import YesNoDialog from '../dialogs/yes-no-dialog';
import { ZoneMapDetails } from './zone-map-details';
import {
  GetAssetMapImagesByMapId,
  GetDetailMapImageByMapImageId,
  RemoveMapImageAsync,
  UpdateMapImagesAsync
} from '../../utils/game-document/assets/map-images';
import { MapConfigStore } from '../../pages/game-designer';
import { YesNoDialogResult } from '../../types/dialog-result';
import { MapContext } from '../../components/map/map-context';
import WorldMapEvent from '../../pages/world-map/world-map-event';
import { GameDocument, MapImage } from '../../types/game-document';
import { GameDocumentContext } from '../../contexts/game-document';
import { popupMenu as MenuList } from '../../components/popupMenu';
import { MapIllustrationContext } from './map-illustration-context';
import {
  UploadedImage,
  ResourceWindow
} from '../../features/game-document/image-resource/resource-window';
import { TaskEditorWindow } from '../../features/game-document/tasks';
import { AreaEditorWindow } from '../../features/game-document/areas';
import { ZoneAssetId } from '../../types/game-document/entities/zone';
import { TaskRoute } from '../../types/game-document/entities/routes';
import { repositionAllToIllustration } from '../../utils/map/objects-position';
import { useGameDocumentResources } from '../../hooks/use-game-document-resources';
import { MapImageWindow } from '../../features/game-document/maps/map-images-window';
import { TaskPreviewDialog } from '../dialogs/task-preview-dialog/task-preview-dialog';
import { RouteEditorWindow } from '../../features/game-document/maps/map-routes-window';

type MapsOptions = 'gis' | 'illustrated';
type MapAction = 'Zone' | 'Task' | 'Area' | 'Annotation' | 'Images' | '';

const zoneAreaMenu: MenuList[] = [
  { classIcon: 'edit', textMenu: 'Edit', textClass: '' },
  { classIcon: 'delete', textMenu: 'Delete', textClass: 'text-danger' }
];

const taskMenu: MenuList[] = [
  { classIcon: 'edit', textMenu: 'Edit', textClass: '' },
  { classIcon: 'visibility', textMenu: 'Preview', textClass: '' },
  { classIcon: 'content_copy', textMenu: 'Copy', textClass: '' },
  { classIcon: 'delete', textMenu: 'Delete', textClass: 'text-danger' }
];

const imageMenu: MenuList[] = [
  { classIcon: 'edit', textMenu: 'Edit', textClass: '' },
  { classIcon: 'lock', textMenu: 'Lock', textClass: '' },
  { classIcon: 'delete', textMenu: 'Delete', textClass: 'text-danger' }
];

interface MapEditorProps {
  zoneId?: string;
  mapInfo?: MapEntity;
  zones?: ZoneEntity[];
  mapOptions?: MapsOptions;
  showMapAccordion?: boolean;
  showZonesAccordion?: boolean;
  showAreasAccordion?: boolean;
  showTasksAccordion?: boolean;
  currentImageResource?: string;
  selectedAllObjectType?: string;
  showAllZones: boolean;
  showAllAreas: boolean;
  isShowZoneNameVisibility: boolean;
  isShowAreaNameVisibility: boolean;
  taskPopupId?: string;
  showTaskTitle: boolean;
  vectorPopupId?: string;
  setShowAllZones: () => void;
  setShowAllAreas: () => void;
  toggleShowTaskTitle: () => void;
  removeTaskOverlay: (id: string) => void;
  onSelectedPanel?: (e: MapAction) => void;
  onChangeMapOptions?: (e: MapsOptions) => void;
  onSelectAllAssets?: (selected: string) => void;
  onCenterMap?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  updateTaskOverlayPosition: (
    id: string,
    newPosition: Coordinate | number[]
  ) => void;
  setIsShowZoneNameVisibility: React.Dispatch<React.SetStateAction<boolean>>;
  setIsShowAreaNameVisibility: React.Dispatch<React.SetStateAction<boolean>>;
  onTaskPositionUpdated?: (taskId: string, lat: number, lon: number) => void;
}

const MapEditor = ({
  zones,
  mapInfo,
  mapOptions,
  zoneId = '',
  taskPopupId,
  showTaskTitle,
  vectorPopupId,
  showAllZones,
  showAllAreas,
  showMapAccordion,
  showZonesAccordion,
  showTasksAccordion,
  showAreasAccordion,
  currentImageResource,
  selectedAllObjectType,
  isShowAreaNameVisibility,
  isShowZoneNameVisibility,
  onCenterMap,
  onSelectedPanel,
  setShowAllZones,
  setShowAllAreas,
  removeTaskOverlay,
  onChangeMapOptions,
  toggleShowTaskTitle,
  updateTaskOverlayPosition,
  setIsShowAreaNameVisibility,
  setIsShowZoneNameVisibility,
  onSelectAllAssets = () => {}
}: MapEditorProps) => {
  const { gameId } = useParams();
  const { taskId } = useParams();
  const { state: stateLocation } = useLocation();
  const navigate = useNavigate();

  const [mapConfig, setMapConfig] = useLocalStorage<MapConfigStore>(
    `map-config-${gameId}`,
    {}
  );

  const [state, setState] = useContext(GameDocumentContext);
  const gameDocumentFiles = useGameDocumentResources();
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [actionsEventDialogVisible, setActionsEventDialogVisible] =
    useState<boolean>(false);
  const [showDeleteIllustrationConfirm, setShowDeleteIllustrationConfirm] =
    useState<boolean>(false);
  const [showDeleteTaskConfirm, setShowDeleteTaskConfirm] =
    useState<boolean>(false);
  const map = useContext(MapContext);
  const mapIllustration = useContext(MapIllustrationContext);
  const [titleDialog, setTitleDialog] = useState<string>('');
  const [selectedPanel, setSelectedPanel] = useState<MapAction>('');
  const [isShowTasknameVisibility, setIsShowTasknameVisibility] =
    useState<boolean>(state.gameDocument?.overview?.showTasknameVisibility!);
  const [selectedAreaId, setSelectedAreaId] = useState<string>('');
  const [selectedAreaTitle, setSelectedAreaTitle] = useState<
    ResourceEntity | undefined
  >(undefined);
  const [showDeleteAreaConfirmation, setShowDeleteAreaConfirmation] =
    useState<boolean>(false);
  const [actionsAreaDialogVisible, setActionsAreaDialogVisible] =
    useState<boolean>(false);
  const [actionsRoutesDialogVisible, setActionsRoutesDialogVisible] =
    useState<boolean>(false);
  const [actionsMapImageDialogVisible, setActionsMapImageDialogVisible] =
    useState<boolean>(false);
  const [actionsEditTaskDialogVisible, setActionsEditTaskDialogVisible] =
    useState<boolean>(false);
  const [currentTaskIdForDelete, setCurrentTaskIdForDelete] =
    useState<string>('');
  const { gameDocument } = state;
  const [imageResource, setImageResource] = useState<ResourceEntity>({
    id: '',
    name: '',
    description: ''
  });
  const [entityEditorValue, setEntityEditorValue] =
    useState<EntityEditor<TaskEntity>>();
  const [resourceEditorValue, setResourceEditorValue] =
    useState<EntityEditor<ResourceEntity>[]>();
  const [selectedRouteId, setSelectedRouteId] = useState<string>('');
  const [selectedMapImageId, setSelectedMapImageId] = useState<string>('');
  const [showDeleteRouteConfirmation, setShowDeleteRouteConfirmation] =
    useState<boolean>(false);
  const [showDeleteMapImageConfirmation, setShowDeleteMapImageConfirmation] =
    useState<boolean>(false);
  const [ableUploadImage, setAbleUploadImage] = useState<boolean>(false);
  const [showPreviewTask, setShowPreviewTask] = useToggle(false);
  const [liveTaskCoords, setLiveTaskCoords] = useState<
    { lat: number; lon: number } | undefined
  >();
  const [showAllTasks, toggleShowAllTasks] = useToggle(
    mapConfig.showAllTasks ?? false
  );

  const toggleRouteDeleteDialog = () => {
    setShowDeleteRouteConfirmation(!showDeleteRouteConfirmation);
  };

  const toggleMapImageDeleteDialog = () => {
    setShowDeleteMapImageConfirmation(!showDeleteMapImageConfirmation);
  };

  const getQueryParam = (name: string): string | null => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    return urlSearchParams.get(name);
  };

  const areaId = getQueryParam('areaId');
  const [areaPopupHasOpened, setAreaPopupHasOpened] = useState<boolean>(false);

  const routes = React.useMemo(() => {
    if (!gameDocument) return [];

    let mapAssetId = '';

    if (zoneId) {
      mapAssetId =
        gameDocument?.rules.worldMap.zones.find(
          (zone: ZoneAssetId) => zone.zoneAssId === zoneId
        )?.mapAssId ?? '';
    } else {
      mapAssetId = gameDocument?.rules?.worldMap?.mapAssId!;
    }
    return GetAssetRoutesByMapId(gameDocument!, mapAssetId)!;
  }, [gameDocument, zoneId]);

  const onConfirmDeleteRoute = (result: YesNoDialogResult) => {
    if (result === 'yes') {
      if (gameDocument) {
        if (state.gameDocument) {
          if (zoneId !== '') {
            const zoneMapAssetId: string =
              state.gameDocument?.rules.worldMap.zones.find(
                (zone: ZoneAssetId) => zone.zoneAssId === zoneId
              )?.mapAssId ?? '';

            GetDetailRoutesById(
              state?.gameDocument!,
              zoneMapAssetId,
              selectedRouteId
            ).then((routeEntity) => {
              if (!routeEntity) {
                return;
              }
              RemoveRoutes(routeEntity as TaskRoute);
            });
          } else {
            GetDetailRoutesByRouteId(
              state?.gameDocument!,
              selectedRouteId
            ).then((routeEntity) => {
              if (!routeEntity) {
                return;
              }
              RemoveRoutes(routeEntity as TaskRoute);
            });
          }
        }
      }
    }
    toggleRouteDeleteDialog();
  };
  const onConfirmDeleteMapImage = (result: YesNoDialogResult) => {
    if (result === 'yes') {
      if (gameDocument) {
        if (state.gameDocument) {
          GetDetailMapImageByMapImageId(
            state?.gameDocument!,
            selectedMapImageId
          ).then((response) => {
            RemoveMapImage(response as MapImage);
          });
        }
      }
    }
    toggleMapImageDeleteDialog();
  };

  const tasks = React.useMemo(() => {
    if (zoneId) {
      return GetTasksByZoneId(zoneId, gameDocument) ?? [];
    } else {
      const taskIds = gameDocument?.rules?.worldMap?.tasks?.map(
        (task) => task.taskAssId
      );

      if (taskIds) return GetTasksByIds(gameDocument, taskIds) ?? [];
    }

    return [];
  }, [zoneId, gameDocument]);

  const areas = React.useMemo(() => {
    if (!gameDocument) return [];

    if (zoneId) {
      return GetAreasByZoneId(gameDocument, zoneId);
    } else {
      return GetAreasWorldMap(gameDocument);
    }
  }, [gameDocument, zoneId]);

  const mapImages = React.useMemo(() => {
    if (!state.gameDocument) return [];

    let mapAssetId = '';

    if (zoneId !== '') {
      mapAssetId =
        state.gameDocument?.rules.worldMap.zones.find(
          (zone: ZoneAssetId) => zone.zoneAssId === zoneId
        )?.mapAssId ?? '';
      return GetAssetMapImagesByMapId(gameDocument!, mapAssetId)!;
    } else {
      mapAssetId = state?.gameDocument?.rules?.worldMap?.mapAssId!;
      return GetAssetMapImagesByMapId(gameDocument!, mapAssetId)!;
    }

    return [];
  }, [state.gameDocument, zoneId]);

  // #region "Zone"

  const [selectedZone, setSelectedZone] = useState<string>('');
  const [showDeleteZoneConfirmation, setShowDeleteZoneConfirmation] =
    useState<boolean>(false);

  const handleZoneMenuSelected = (id: string, menu: MenuList) => {
    if (menu.textMenu === 'Edit') {
      setSelectedZone(id);
      setMapConfig((prev) => ({ ...prev, zoneId: id }));

      if (gameDocument) {
        const zone = GetZoneById(gameDocument, id);
        navigate(`/designer/${gameId}/map/${zone?.id}`);
      }
    } else if (menu.textMenu === 'Delete') {
      setSelectedZone(id);
      setShowDeleteZoneConfirmation(true);
    }
  };

  const removeZoneAreaGeometry = (boundaryId: string) => {
    const mapContext = mapInfo?.type === 'illustrated' ? mapIllustration : map;

    const layer = mapContext
      .getAllLayers()
      .find((l) => l.get('id') === 'vectorZoneArea') as VectorLayer<
      VectorSource<Feature<Geometry>>,
      Feature<Geometry>
    >;

    if (layer)
      layer
        .getSource()
        ?.getFeatures()
        .forEach((f) => {
          if (f.getId() === boundaryId) layer.getSource()?.removeFeature(f);
        });

    const label = mapContext.getOverlayById(boundaryId);

    if (label) mapContext.removeOverlay(label);
  };

  const onConfirmDeleteZone = async (result: YesNoDialogResult) => {
    if (result === 'yes') {
      if (gameDocument) {
        //Delete zone from asset.zone
        const zone = GetZoneById(gameDocument, selectedZone);
        DeleteZoneAsync(state.gameDocument!, zone?.id!).then(
          (updatedGameDocument) => {
            setState((prev) => UpdateGameDocState(prev, updatedGameDocument));
          }
        );

        if (zone) removeZoneAreaGeometry(`zone-${zone.boundary.id}`);
      }
    }

    setShowDeleteZoneConfirmation(false);
  };

  // #endregion "Zone"

  // #region "Area"
  const toggleActionsAreaDialog = () => {
    setActionsAreaDialogVisible(!actionsAreaDialogVisible);
    setSelectedPanel('');
    onSelectedPanel && onSelectedPanel('');
  };

  const toggleActionsRouteDialog = () => {
    setActionsRoutesDialogVisible(!actionsRoutesDialogVisible);
    setSelectedPanel('');
    onSelectedPanel && onSelectedPanel('');
  };

  const toggleActionsMapImageDialog = () => {
    setActionsMapImageDialogVisible(!actionsMapImageDialogVisible);
    setSelectedPanel('');
    onSelectedPanel && onSelectedPanel('');
  };

  const loadAreaForEdit = (id: string) => {
    setSelectedAreaId(id);
    const areaData = GetAreaById(gameDocument, id);
    if (areaData) {
      const areaTitle = state.gameDocument?.resources.find(
        (r) => r.id === areaData.titleResId
      );
      setSelectedAreaTitle(areaTitle);
      setTitleDialog(areaData.name as any);
      toggleActionsAreaDialog();
    }
  };

  const handleAreaMenuSelected = (id: string, menu: MenuList) => {
    if (menu.textMenu === 'Edit') {
      loadAreaForEdit(id);
    } else if (menu.textMenu === 'Delete') {
      setSelectedAreaId(id);
      setShowDeleteAreaConfirmation(true);
    }
  };

  const onConfirmDeleteArea = async (result: YesNoDialogResult) => {
    if (result === 'yes') {
      if (gameDocument) {
        const selectedArea = GetAreaById(gameDocument, selectedAreaId);
        //Delete area from asset
        const resultArea = await DeleteAreaAsync(
          gameDocument,
          selectedArea?.id ?? ''
        );

        if (zoneId) {
          //delete area from asset.zones.area
          removeAreaFromZone(resultArea, selectedAreaId);
        } else {
          setState((prev) => UpdateGameDocState(prev, resultArea));
        }

        if (selectedArea)
          removeZoneAreaGeometry(`area-${selectedArea.boundary.id}`);
      }
    }

    setShowDeleteAreaConfirmation(false);
  };

  const removeAreaFromZone = async (
    gameDocument: GameDocument,
    areaId: string
  ) => {
    if (gameDocument) {
      const zone = GetZoneById(gameDocument, zoneId);

      if (zone) {
        const areaIndex = zone?.areas?.findIndex((x) => x.areaAssId === areaId);
        zone.areas?.splice(areaIndex!, 1);

        const resultZone = await UpdateZoneAsync(gameDocument, zoneId, zone);
        setState((prev) => UpdateGameDocState(prev, resultZone));
      }
    }
  };

  // #endregion "Area"

  // #region "Task"
  const handleTaskEntity = (
    editorEntity: EntityEditor<TaskEntity>,
    resourceEntity: EntityEditor<ResourceEntity>[]
  ) => {
    const latestTask = gameDocument?.assets?.tasks?.find(
      (t) => t.id === editorEntity.entity.id
    );
    if (latestTask?.boundary) {
      editorEntity.entity.boundary = latestTask.boundary;
    }
    SaveEntityEditorAsync(
      state?.gameDocument!,
      editorEntity,
      resourceEntity
    ).then((response) => {
      if (response) {
        setState((prev) => UpdateGameDocState(prev, response!));
      }

      toggleEditTaskDialog();
    });
  };

  const isAbleToUploadImage = async () => {
    let map = await getMapEntityAsync();

    if (map?.type !== 'illustrated') {
      setAbleUploadImage(true);
    } else {
      if (!map?.imageResId) {
        setAbleUploadImage(false);
      } else {
        setAbleUploadImage(true);
      }
    }
  };

  const onConfirmDeleteTask = async (result: YesNoDialogResult) => {
    if (result === 'yes') {
      if (gameDocument) {
        let selectedTask = GetTaskById(gameDocument, currentTaskIdForDelete);
        if (selectedTask) {
          const result = await RemoveTaskBoundaryAsync(
            gameDocument,
            selectedTask.id
          );

          //delete annotation that assosiate with this deleted task
          const routes = cloneDeep(result.assets.routes ?? []);
          const filtered = routes.filter(
            (r) =>
              !(
                r.fromTaskId === selectedTask.id ||
                r.toTaskId === selectedTask.id
              )
          );
          result.assets.routes = filtered;

          if (!zoneId) {
            //delete task from worldmap.tasks
            let selectedTaskIndex = result?.rules?.worldMap?.tasks?.findIndex(
              (x) => x.taskAssId === selectedTask.id
            );

            result?.rules?.worldMap?.tasks?.splice(selectedTaskIndex!, 1);
            setState((prev) => UpdateGameDocState(prev, result!));
          } else {
            //delete task from asset.zones.tasks
            await removeTaskFromZone(result);
          }

          removeTaskOverlay(selectedTask.id);
        }
      }
    }

    setShowDeleteTaskConfirm(false);
  };

  const removeTaskFromZone = async (gameDocument: GameDocument) => {
    if (zoneId && gameDocument) {
      let zone = GetZoneById(gameDocument, zoneId);

      if (zone) {
        let selectedTask = GetTaskById(gameDocument, currentTaskIdForDelete!);
        let selectedTaskIndex = zone?.tasks?.findIndex(
          (x) => x.taskAssId === selectedTask?.id
        );

        zone.tasks?.splice(selectedTaskIndex!, 1);
        const resultZone = await UpdateZoneAsync(gameDocument, zoneId, zone);
        setState((prev) => UpdateGameDocState(prev, resultZone));
      }
    }
  };

  const [selectedTask, setSelectedTask] = useState<TaskEntity>();

  const handleTaskMenuSelected = (id: string, menu: MenuList) => {
    const taskData = GetTaskById(gameDocument!, id);
    if (menu.textMenu === 'Edit') {
      setActionsEditTaskDialogVisible(true);
      setSelectedTask(taskData);
      onEditTaskEntity(taskData!);

      if (selectedTask) {
        setTitleDialog(selectedTask.name);
      }
      if (taskData?.boundary?.geometry?.coordinates) {
        const [lon, lat] = toLonLat(taskData.boundary.geometry.coordinates);
        setLiveTaskCoords({ lat, lon });
      } else {
        setLiveTaskCoords(undefined);
      }
    } else if (menu.textMenu === 'Copy') {
      setSelectedPanel('Task');
      copyTask(id);
    } else if (menu.textMenu === 'Delete') {
      setCurrentTaskIdForDelete(id);
      setShowDeleteTaskConfirm(true);
    } else if (menu.textMenu === 'Preview') {
      setSelectedTask(taskData);
      setShowPreviewTask();
    }
  };

  const copyTask = async (taskId: string) => {
    if (!taskId) return;

    let newTaskId = uuid();

    if (zoneId !== '') {
      let zone = await GetZoneById(state?.gameDocument, zoneId);

      if (!zone) return;

      if (taskId) {
        const newGameDocument = await CopyTaskAsync(
          gameDocument!,
          taskId,
          newTaskId
        );

        zone?.tasks?.push({ taskAssId: newTaskId });

        const resultZone = await UpdateZoneAsync(
          newGameDocument!,
          zoneId,
          zone!
        );
        setState((prevState) => UpdateGameDocState(prevState, resultZone));
      }
    } else {
      if (taskId) {
        const newGameDocument = await CopyTaskAsync(
          gameDocument!,
          taskId,
          newTaskId
        );
        newGameDocument?.rules?.worldMap?.tasks.push({ taskAssId: newTaskId });
        setState((prev) => UpdateGameDocState(prev, newGameDocument!));
      }
    }
  };

  const handleRouteMenuSelected = (id: string, menu: MenuList) => {
    setSelectedRouteId(id);
    if (menu.textMenu === 'Edit') {
      toggleActionsRouteDialog();
    } else if (menu.textMenu === 'Delete') {
      toggleRouteDeleteDialog();
    }
  };

  const handleMapImagesMenuSelected = (id: string, menu: MenuList) => {
    setSelectedMapImageId(id);
    if (menu.textMenu === 'Edit') {
      toggleActionsMapImageDialog();
    } else if (menu.textMenu === 'Delete') {
      toggleMapImageDeleteDialog();
    } else if (['Lock', 'Un-lock'].includes(menu.textMenu ?? '')) {
      const selectedImage = mapImages.find((x) => x.id === id) as MapImage;

      const image: MapImage = {
        ...selectedImage,
        isLocked: menu.textMenu === 'Lock'
      };

      updateMapImageAsset(image);
    }
  };

  const handleHideShowObjectTitle = (
    type: 'zone' | 'area' | 'task',
    value: boolean
  ) => {
    const titleProp: Record<
      typeof type,
      | 'showZonenameVisibility'
      | 'showAreanameVisibility'
      | 'showTasknameVisibility'
    > = {
      zone: 'showZonenameVisibility',
      area: 'showAreanameVisibility',
      task: 'showTasknameVisibility'
    };

    if (type === 'zone') setIsShowZoneNameVisibility(value);
    if (type === 'area') setIsShowAreaNameVisibility(value);
    if (type === 'task') setIsShowTasknameVisibility(value);

    let updatedGameDocument = state.gameDocument;
    if (updatedGameDocument?.overview) {
      const overviewProp = titleProp[type];
      updatedGameDocument.overview[overviewProp] = value;
      setState((state) => ({
        ...state,
        isDirty: true,
        gameDocument: updatedGameDocument
      }));
    }

    if (type === 'task') {
      const taskContainer = document.querySelectorAll('[id^="taskContainer-"]');
      taskContainer.forEach((task) => {
        const detailTaskContainer = task.querySelector(
          '[id^="detailTaskContainer-"]'
        );
        const taskName = task.querySelector('[id^="taskName-"]');
        if (isShowTasknameVisibility) {
          task.classList.remove('expanded');
          taskName?.classList.add('d-none');
          detailTaskContainer?.classList.remove('visible');
          detailTaskContainer?.classList.add('hidden');
        } else {
          task.classList.add('expanded');
          taskName?.classList.remove('d-none');
          detailTaskContainer?.classList.remove('hidden');
          detailTaskContainer?.classList.add('visible');
        }
      });
    }
  };

  const onEditTaskEntity = (entity: TaskEntity) => {
    if (entity) {
      setEntityEditorValue({
        isNew: false,
        entity: entity
      });

      let resources: EntityEditor<ResourceEntity>[] = [];

      for (const res of GetResourceKeys(entity)) {
        resources.push({
          isNew: false,
          entity: GetResourceEntity(state.gameDocument!, entity[res] as string)
        });
      }

      setResourceEditorValue(resources);
    }
  };

  const toggleEditTaskDialog = () => {
    if (actionsEditTaskDialogVisible) {
      setLiveTaskCoords(undefined);
    }
    setActionsEditTaskDialogVisible(!actionsEditTaskDialogVisible);
  };

  const handleTaskCoordinateSave = async (
    taskId: string,
    lat: number,
    lon: number
  ) => {
    if (!gameDocument) return;
    const task = gameDocument.assets.tasks?.find((t) => t.id === taskId);
    if (!task || !task.boundary) return;

    task.boundary.geometry.coordinates = fromLonLat([lon, lat]);
    const result = await UpdateTaskAsync(gameDocument, task.id, task);
    setState((prev) => UpdateGameDocState(prev, result));
    updateTaskOverlayPosition(task.id, [lon, lat]);
  };

  //#endregion "Task"

  const getWorldMapAsset = async () => {
    return await GetMapByIdAsync(
      state?.gameDocument!,
      state?.gameDocument?.rules?.worldMap?.mapAssId!
    );
  };

  const updateMapAsset = (map: MapEntity) => {
    UpdateMapAsync(state?.gameDocument!, map.id, map).then((response) => {
      setState((prev) => UpdateGameDocState(prev, response));
    });
  };

  const getAssetRoutes = async () => {
    return await GetDetailRoutesByMapId(
      state?.gameDocument!,
      state?.gameDocument?.rules?.worldMap?.mapAssId!
    );
  };

  const updateRoutesAsset = (route: TaskRoute) => {
    const result = UpdateRoutesAsync(state?.gameDocument!, route);
    setState((prev) => UpdateGameDocState(prev, result));
  };

  const RemoveRoutes = (route: TaskRoute) => {
    RemoveRoutesAsync(state?.gameDocument!, route).then((response) => {
      setState((prev) => UpdateGameDocState(prev, response));
    });
  };
  const RemoveMapImage = async (mapImage: MapImage) => {
    const response = await DeleteResourceAsync(
      state.gameDocument!,
      mapImage.titleResId!
    );
    await RemoveMapImageAsync(response!, mapImage).then((response) => {
      setState((prev) => UpdateGameDocState(prev, response));
    });
  };

  const updateMapImageAsset = (mapImage: MapImage) => {
    UpdateMapImagesAsync(state.gameDocument!, mapImage).then((response) => {
      setState((prev) => UpdateGameDocState(prev, response));
    });
  };

  const updateMapResource = async (
    mapEntity: MapEntity,
    uploadedImage: UploadedImage
  ) => {
    if (mapEntity.imageResId) {
      gameDocumentFiles.updateResourceFile(mapEntity.imageResId, uploadedImage);
    } else {
      const resourceId = uuid();
      mapEntity.imageResId = resourceId;
      const response = await AddResourceAsync(
        state.gameDocument!,
        uploadedImage.name || uploadedImage.fileName || '',
        uploadedImage.fileName ?? '',
        'image',
        uploadedImage.blobUrl,
        resourceId,
        uploadedImage.size,
        uploadedImage.category
      );
      setState((prev) => UpdateGameDocState(prev, response));
    }
  };

  const handleFilesSelected = async (uploadedImage: UploadedImage) => {
    setDialogVisible(false);
    if (currentImageResource === uploadedImage.blobUrl) return;

    let currentExt: number[];
    if (currentImageResource) {
      const currentImg = new Image();
      currentImg.src = currentImageResource ?? '';
      currentImg.onload = () => {
        currentExt = [0, 0, currentImg.width, currentImg.height];
      };
    }

    const img = new Image();
    img.src = uploadedImage.blobUrl;

    if (currentImageResource) {
      img.onload = () => {
        const newExtent = [0, 0, img.width, img.height];
        const scaleX = newExtent[2] / currentExt[2];
        const scaleY = newExtent[3] / currentExt[3];

        // Update tasks coordinates
        if (tasks) {
          tasks.forEach(async (task) => {
            if (task.boundary?.geometry?.coordinates) {
              const [x, y] = task.boundary.geometry.coordinates;
              const newCoord = [x * scaleX, y * scaleY];
              task.boundary.geometry.coordinates = newCoord;

              const result = await UpdateTaskAsync(
                gameDocument!,
                task.id,
                task
              );
              setState((state) => UpdateGameDocState(state, result));

              updateTaskOverlayPosition(task.id, toLonLat(newCoord));
            }
          });
        }

        // Update zones coordinates
        if (zones) {
          zones.forEach(async (zone) => {
            if (zone.boundary?.geometry?.coordinates) {
              const newCoord = zone.boundary.geometry.coordinates[0].map(
                (coord: [number, number]) => {
                  const [x, y] = coord;
                  return [x * scaleX, y * scaleY];
                }
              );
              zone.boundary.geometry.coordinates[0] = newCoord;

              const result = await UpdateZoneAsync(
                gameDocument!,
                zone.id,
                zone
              );
              setState((state) => UpdateGameDocState(state, result));
            }
          });
        }

        if (areas) {
          areas.forEach((area) => {
            if (area.boundary?.geometry?.coordinates) {
              const newCoord = area.boundary.geometry.coordinates[0].map(
                (coord: [number, number]) => {
                  const [x, y] = coord;
                  return [x * scaleX, y * scaleY];
                }
              );
              area.boundary.geometry.coordinates[0] = newCoord;

              const result = UpdateAreaAsync(gameDocument!, area.id, area);
              setState((state) => UpdateGameDocState(state, result));
            }
          });
        }
      };
    } else {
      img.onload = () => {
        const imgWidth = img.width;
        const imgHeight = img.height;

        let updatedGameDocument = cloneDeep(state.gameDocument);

        const { tasks, areas, zones } = repositionAllToIllustration(
          updatedGameDocument?.assets,
          imgWidth,
          imgHeight,
          0.5
        );

        let areaIds: string[] = [];
        let taskIds: string[] = [];
        if (zoneId) {
          const tasksZone = GetTasksByZoneId(zoneId, state.gameDocument);
          const areasZone = GetAreasByZoneId(state.gameDocument, zoneId);
          taskIds = tasksZone.map((t) => t.id);
          areaIds = areasZone.map((a) => a.id);
        } else {
          taskIds =
            state.gameDocument?.rules?.worldMap?.tasks?.map(
              (task) => task.taskAssId
            ) ?? [];
          const areaWorld = GetAreasWorldMap(state.gameDocument);
          areaIds = areaWorld.map((a) => a.id);
        }

        if (updatedGameDocument && state.gameDocument) {
          const filteredAreas = areas.filter((a) => areaIds.includes(a.id));
          const filteredTasks = tasks.filter((t) => taskIds.includes(t.id));

          updatedGameDocument.assets = {
            ...state.gameDocument.assets,
            ...(!zoneId && { zones }),
            areas: [
              ...(state.gameDocument.assets.areas ?? []).filter(
                (a) => !areaIds.includes(a.id)
              ),
              ...filteredAreas
            ],
            tasks: [
              ...(state.gameDocument.assets.tasks ?? []).filter(
                (t) => !taskIds.includes(t.id)
              ),
              ...filteredTasks
            ]
          };

          setState((state) => UpdateGameDocState(state, updatedGameDocument));
        }
      };
    }

    if (state.gameDocument) {
      if (zoneId !== '') {
        // Zone map upload
        const zoneMapAssetId: string =
          state.gameDocument?.rules.worldMap.zones.find(
            (zone: ZoneAssetId) => zone.zoneAssId === zoneId
          )?.mapAssId ?? '';
        const mapEntity: MapEntity =
          (await GetMapByIdAsync(state.gameDocument, zoneMapAssetId)) ??
          ({} as MapEntity);
        mapEntity.zoomLevel = 1;
        await updateMapResource(mapEntity, uploadedImage);
        updateMapAsset(mapEntity);
      } else {
        // World map upload
        const mapEntity: MapEntity =
          (await getWorldMapAsset()) ?? ({} as MapEntity);
        mapEntity.zoomLevel = 1;
        await updateMapResource(mapEntity, uploadedImage);
        updateMapAsset(mapEntity);
      }
      populateImageResource();
    }
  };

  const getMapEntityAsync = async () => {
    if (zoneId) {
      const mapAssId = gameDocument?.rules.worldMap.zones.find(
        (z) => z.zoneAssId === zoneId
      )?.mapAssId;
      return await GetMapByIdAsync(state?.gameDocument!, mapAssId!);
    } else {
      return await getWorldMapAsset();
    }
  };

  const onHandleMapOptionChange = (e: RadioButtonChangeEvent) => {
    onChangeMapOptions && onChangeMapOptions(e.value);
  };

  const toggleDialog = () => {
    setDialogVisible(!dialogVisible);
  };

  const toggleActionsEventDialogVisible = useCallback(() => {
    setActionsEventDialogVisible(!actionsEventDialogVisible);
  }, [actionsEventDialogVisible]);

  const onHandleOpenMapIllustration = () => {
    setShowDeleteIllustrationConfirm(true);
  };

  const onConfirmDeleteIllustration = async (result: YesNoDialogResult) => {
    if (result === 'yes') {
      if (zoneId !== '') {
        const mapAssId = gameDocument?.rules.worldMap.zones.find(
          (z) => z.zoneAssId === zoneId
        )?.mapAssId;
        const updatedMap = await GetMapByIdAsync(
          state?.gameDocument!,
          mapAssId!
        );
        if (updatedMap) {
          const response = await DeleteResourceAsync(
            state.gameDocument!,
            updatedMap.imageResId!
          );
          setState((prev) => UpdateGameDocState(prev, response));
          updatedMap.imageResId = '';
          updateMapAsset(updatedMap!);
        }
      } else {
        let mapEntity = await getWorldMapAsset();
        if (mapEntity) {
          const response = await DeleteResourceAsync(
            state.gameDocument!,
            mapEntity.imageResId!
          );
          setState((prev) => UpdateGameDocState(prev, response));
          mapEntity.imageResId = '';
          updateMapAsset(mapEntity);
        }
      }
    }

    setShowDeleteIllustrationConfirm(false);
  };

  const getBoundsZoomLevel = (
    viewPort: { northEast: Coordinate; southWest: Coordinate },
    mapDim: { height: number; width: number }
  ) => {
    let WORLD_DIM = { height: 256, width: 256 };
    let ZOOM_MAX = 21;

    const latRad = (lat: number) => {
      let sin = Math.sin((lat * Math.PI) / 180);
      let radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
      return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
    };

    const zoom = (mapPx: number, worldPx: number, fraction: number) => {
      return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
    };

    let ne = viewPort.northEast;
    let sw = viewPort.southWest;

    let latFraction = (latRad(ne.lat) - latRad(sw.lat)) / Math.PI;

    let lngDiff = ne.lng - sw.lng;
    let lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

    let latZoom = zoom(mapDim.height, WORLD_DIM.height, latFraction);
    let lngZoom = zoom(mapDim.width, WORLD_DIM.width, lngFraction);

    return Math.min(latZoom, lngZoom, ZOOM_MAX);
  };

  const updateBoundaryCoordinates = (
    coordinates: number[][],
    currentCenter: number[],
    selectedLon: number,
    selectedLat: number
  ) => {
    return coordinates.map((coordinate) => {
      const offsetX = coordinate[0] - currentCenter[0];
      const offsetY = coordinate[1] - currentCenter[1];
      return [selectedLon + offsetX, selectedLat + offsetY];
    });
  };

  const calculateNewCoordinates = (
    currentCenter: number[],
    prevCoordinates: [number, number],
    selectedLon: number,
    selectedLat: number
  ): [number, number] => {
    const offsetX = prevCoordinates[0] - currentCenter[0];
    const offsetY = prevCoordinates[1] - currentCenter[1];

    return [selectedLon + offsetX, selectedLat + offsetY];
  };

  const updateTask = async (
    task: TaskEntity,
    currentCenter: number[],
    selectedLon: number,
    selectedLat: number,
    gameDocument: any
  ) => {
    if (task.boundary) {
      let boundary = task.boundary;

      boundary.geometry.coordinates = calculateNewCoordinates(
        currentCenter,
        boundary.geometry.coordinates as [number, number],
        selectedLon,
        selectedLat
      );

      task.boundary = boundary;
      if (gameDocument) {
        const result = await UpdateTaskAsync(gameDocument, task.id, task);
        setState((state) => UpdateGameDocState(state, result));
        updateTaskOverlayPosition(
          task.id,
          toLonLat(boundary.geometry.coordinates)
        );
      }
    }
  };

  const updateZone = async (
    zone: ZoneEntity,
    currentCenter: number[],
    selectedLon: number,
    selectedLat: number,
    gameDocument: any
  ) => {
    if (zone.boundary) {
      zone.boundary.geometry.coordinates[0] = updateBoundaryCoordinates(
        zone.boundary.geometry.coordinates[0],
        currentCenter,
        selectedLon,
        selectedLat
      );
      if (gameDocument) {
        const resultZone = await UpdateZoneAsync(gameDocument, zone.id, zone);
        setState((prev) => UpdateGameDocState(prev, resultZone));
      }
    }
  };

  const updateArea = (
    area: AreaEntity,
    currentCenter: number[],
    selectedLon: number,
    selectedLat: number,
    gameDocument: any
  ) => {
    if (area.boundary) {
      area.boundary.geometry.coordinates[0] = updateBoundaryCoordinates(
        area.boundary.geometry.coordinates[0],
        currentCenter,
        selectedLon,
        selectedLat
      );
      if (gameDocument) {
        const resultArea = UpdateAreaAsync(gameDocument, area.id, area);
        setState((prev) => UpdateGameDocState(prev, resultArea));
      }
    }
  };

  const updateImage = (
    image: MapImage,
    currentCenter: number[],
    selectedLon: number,
    selectedLat: number,
    gameDocument: any
  ) => {
    const imageCenter = image.center!;
    const offsetX = imageCenter[0] - currentCenter[0];
    const offsetY = imageCenter[1] - currentCenter[1];

    image.center = [selectedLon + offsetX, selectedLat + offsetY];

    if (image.extent) {
      const [minX, minY, maxX, maxY] = image.extent;
      image.extent = [
        selectedLon + (minX - currentCenter[0]),
        selectedLat + (minY - currentCenter[1]),
        selectedLon + (maxX - currentCenter[0]),
        selectedLat + (maxY - currentCenter[1])
      ];
    }

    if (gameDocument) {
      UpdateMapImagesAsync(gameDocument, image).then((response) => {
        setState((prev) => UpdateGameDocState(prev, response));
      });
    }
  };

  const onRecentreLocation = async (
    {
      lon,
      lat
    }: {
      lon: number;
      lat: number;
    },
    contentType: 'all' | 'none' | 'zones' | 'areas' | 'tasks' | 'images'
  ) => {
    try {
      const currentCenter = map.getView().getCenter();
      if (!currentCenter) return;

      const [selectedLon, selectedLat] = fromLonLat([lon, lat]);
      map.getView().setCenter([selectedLon, selectedLat]);

      await Promise.all([
        ...(contentType === 'all' || contentType === 'tasks'
          ? tasks.map((task) =>
              updateTask(
                task,
                currentCenter,
                selectedLon,
                selectedLat,
                gameDocument
              )
            )
          : []),

        ...(contentType === 'all' || contentType === 'zones'
          ? (zones ?? []).map((zone) =>
              updateZone(
                zone,
                currentCenter,
                selectedLon,
                selectedLat,
                gameDocument
              )
            )
          : []),

        ...(contentType === 'all' || contentType === 'areas'
          ? areas.map((area) =>
              updateArea(
                area,
                currentCenter,
                selectedLon,
                selectedLat,
                gameDocument
              )
            )
          : []),

        ...(contentType === 'all' || contentType === 'images'
          ? mapImages.map((image) =>
              updateImage(
                image,
                currentCenter,
                selectedLon,
                selectedLat,
                gameDocument
              )
            )
          : [])
      ]);
    } catch (error) {
      console.error(error);
    }
  };

  const handleEventChange = async (
    algorithmOnEnter: Algorithm,
    algorithmOnExit: Algorithm,
    algorithmOnTaskCompleted: Algorithm
  ) => {
    if (gameDocument === undefined) {
      return;
    }
    const newAlgorithmOnEnter: Algorithm = algorithmOnEnter as Algorithm;
    const newAlgorithmOnExit: Algorithm = algorithmOnExit as Algorithm;
    const newAlgorithmOnTaskCompleted: Algorithm =
      algorithmOnTaskCompleted as Algorithm;
    if (zoneId === '') {
      gameDocument.rules.worldMap.events = {
        onEnterMap: newAlgorithmOnEnter,
        onExitMap: newAlgorithmOnExit,
        onTaskCompletedMap: newAlgorithmOnTaskCompleted
      };
      setState((prev) => UpdateGameDocState(prev, gameDocument));
    } else {
      let zone = await GetZoneById(state?.gameDocument, zoneId);
      if (zone) {
        zone.events = {
          onEnterMap: newAlgorithmOnEnter,
          onExitMap: newAlgorithmOnExit,
          onTaskCompletedMap: newAlgorithmOnTaskCompleted
        };
        const resultZone = await UpdateZoneAsync(gameDocument, zoneId, zone!);
        setState((prevState) => UpdateGameDocState(prevState, resultZone));
      }
    }

    toggleActionsEventDialogVisible();
  };

  const onSetPopupTitle = (title: string) => {
    setTitleDialog(title);
  };

  const EventButtonHandler = useCallback(() => {
    toggleActionsEventDialogVisible();
    onSetPopupTitle('Event configurations');
  }, [toggleActionsEventDialogVisible]);

  const handleToggleZoneChange = (
    e: SwitchChangeEvent,
    toggle: 'isEnableBackButton' | 'isOutlineVisibleWhenPlayerEnter'
  ) => {
    const selectedZone = GetZoneById(gameDocument, zoneId);
    if (selectedZone) {
      if (e.value) {
        selectedZone[toggle] = true;
      } else {
        delete selectedZone[toggle];
      }

      UpdateZoneAsync(gameDocument!, zoneId, selectedZone).then(
        (updatedGameDocument) =>
          setState((prev) => UpdateGameDocState(prev, updatedGameDocument))
      );
    }
  };

  const onChangeEditTimerHandler = (value: string) => {
    if (gameDocument) {
      gameDocument.rules.worldMap.timerAssId = value;
      setState((state) => UpdateGameDocState(state, gameDocument));
    }
  };

  const onChangeEditZoneTimerHandler = async (value: string) => {
    if (gameDocument) {
      // find zones by zoneId

      if (zoneId !== '' || zoneId !== undefined) {
        const zoneIndex = gameDocument?.rules?.worldMap?.zones?.findIndex(
          (x) => x?.zoneAssId === zoneId
        );
        if (zoneIndex !== -1) {
          gameDocument.rules.worldMap.zones[zoneIndex].timerAssId = value;
        }
        setState((state) => UpdateGameDocState(state, gameDocument));
      }
    }
  };

  const populateImageResource = async () => {
    //Edit zone
    if (zoneId !== '') {
      const mapAssId = gameDocument?.rules.worldMap.zones.find(
        (z) => z.zoneAssId === zoneId
      )?.mapAssId;
      if (mapAssId !== undefined || mapAssId !== '') {
        let mapEntity = await GetMapByIdAsync(state?.gameDocument!, mapAssId!);
        if (mapEntity) {
          if (
            mapEntity.imageResId !== undefined ||
            mapEntity.imageResId !== ''
          ) {
            let resource = GetResourceEntity(
              state?.gameDocument!,
              mapEntity.imageResId!
            );
            setImageResource(resource);
          }
        }
      }
    } else {
      let mapEntity = await getWorldMapAsset();
      if (mapEntity) {
        if (mapEntity.imageResId !== undefined || mapEntity.imageResId !== '') {
          let resource = GetResourceEntity(
            state?.gameDocument!,
            mapEntity.imageResId!
          );
          setImageResource(resource);
        }
      }
    }
  };

  const computeImageScale = (
    mapContext: Map,
    imageUrl: string
  ): Promise<[number, number]> => {
    return new Promise((resolve) => {
      const size = mapContext.getSize();
      const resolution = mapContext.getView().getResolution();

      if (!size || !resolution) {
        resolve([1, 1]);
        return;
      }

      const [mapWidthPx, mapHeightPx] = size;
      const VIEW_FILL_RATIO = 0.8;

      const img = new Image();
      img.src = imageUrl;

      img.onload = () => {
        const targetWidthMapUnits = mapWidthPx * resolution * VIEW_FILL_RATIO;
        const targetHeightMapUnits = mapHeightPx * resolution * VIEW_FILL_RATIO;

        const scaleX = targetWidthMapUnits / img.naturalWidth;
        const scaleY = targetHeightMapUnits / img.naturalHeight;

        const uniformScale = Math.min(scaleX, scaleY);
        resolve([uniformScale, uniformScale]);
      };
    });
  };

  // #region "useEffect"

  useEffect(() => {
    isAbleToUploadImage();
  }, [gameDocument]);

  useEffect(() => {
    populateImageResource();
  }, [zoneId, state]);

  useEffect(() => {
    if (taskId) {
      toggleEditTaskDialog();
      const taskData = gameDocument?.assets?.tasks?.find(
        (x) => x.id === taskId
      );

      setSelectedTask(taskData);
      onEditTaskEntity(taskData!);

      if (taskData?.boundary?.geometry?.coordinates) {
        const [lon, lat] = toLonLat(taskData.boundary.geometry.coordinates);
        setLiveTaskCoords({ lat, lon });
      }
    }
    return;
  }, [taskId]);

  useEffect(() => {
    if (areaId && !areaPopupHasOpened) {
      const area = areas?.find((area) => area.id == areaId);
      if (area) {
        loadAreaForEdit(area.id);
        setAreaPopupHasOpened(true);
      }
    }
  }, [areas, areaId]);

  useEffect(() => {
    if (!selectedTask?.id || !actionsEditTaskDialogVisible) return;

    const latestTask = gameDocument?.assets?.tasks?.find(
      (t) => t.id === selectedTask.id
    );

    if (latestTask?.boundary?.geometry?.coordinates) {
      const [lon, lat] = toLonLat(latestTask.boundary.geometry.coordinates);
      setLiveTaskCoords({ lat, lon });
    }
  }, [gameDocument, selectedTask?.id, actionsEditTaskDialogVisible]);

  useEffect(() => {
    const mapContext = mapInfo?.type === 'illustrated' ? mapIllustration : map;
    if (!mapContext) return;

    const existingTaskPopup = mapContext.getOverlayById('task-menu-popup');
    const existingVectorPopup = mapContext.getOverlayById('vector-menu-popup');

    const taskPopup = new Popup({
      id: 'task-menu-popup',
      positioning: 'auto',
      autoPan: {
        animation: {
          duration: 250
        }
      }
    });
    const vectorPopup = new Popup({
      id: 'vector-menu-popup',
      positioning: 'auto',
      autoPan: {
        animation: {
          duration: 250
        }
      }
    });

    if (!existingTaskPopup) mapContext.addOverlay(taskPopup);
    if (!existingVectorPopup) mapContext.addOverlay(vectorPopup);

    return () => {
      mapContext.removeOverlay(taskPopup);
      mapContext.removeOverlay(vectorPopup);
    };
  }, [mapInfo?.type]);

  useEffect(() => {
    if (stateLocation?.openEvents) {
      EventButtonHandler();
    }
  }, [stateLocation?.openEvents]);

  // #endregion "useEffect"

  return (
    <>
      {zoneId && (
        <div
          className={
            'd-flex gap-2 align-items-center p-2 d-flex w-full border-bottom cursor-pointer text-decoration-none'
          }
          onClick={() => {
            setMapConfig((prev) => ({ ...prev, zoneId: '' }));
            navigate(`/designer/${gameId}/map`);
          }}
          style={{
            fontSize: '14px'
          }}>
          <span
            className={'material-symbols-outlined'}
            style={{
              fontSize: '12px'
            }}>
            arrow_back_ios_new
          </span>
          Back to World Map
        </div>
      )}

      <PanelBar
        expandMode={'single'}
        onSelect={(e) =>
          setMapConfig((prev) => ({
            ...prev,
            activePanelBar: e.target.props.id as typeof prev.activePanelBar
          }))
        }
        className={`accordion ${zoneId ? 'zone' : ''}`}>
        {showMapAccordion && (
          <PanelBarItem
            id={'settings'}
            title={'Map settings'}
            expanded={mapConfig.activePanelBar === 'settings'}
            aria-hidden={true}>
            <MapSettings
              mapInfo={mapInfo}
              selectedMapOption={mapOptions!}
              illustrationMapMedia={imageResource}
              onRecentreLocation={onRecentreLocation}
              onSelectIllustrationMapClick={toggleDialog}
              onSelectedMapChange={onHandleMapOptionChange}
              onDeleteIlustrationMap={onHandleOpenMapIllustration}
            />
          </PanelBarItem>
        )}

        {zoneId !== '' && (
          <PanelBarItem
            id={'settings'}
            title={'Zone map settings'}
            expanded={
              stateLocation === null || mapConfig.activePanelBar === 'settings'
            }>
            <MapSettings
              mapInfo={mapInfo}
              selectedMapOption={mapOptions!}
              illustrationMapMedia={imageResource}
              onRecentreLocation={onRecentreLocation}
              onSelectIllustrationMapClick={toggleDialog}
              onSelectedMapChange={onHandleMapOptionChange}
              onDeleteIlustrationMap={onHandleOpenMapIllustration}
            />
          </PanelBarItem>
        )}

        <PanelBarItem
          id={'contents'}
          expanded={mapConfig.activePanelBar === 'contents'}
          title={`${zoneId !== '' ? 'Zone map' : 'Map'} contents`}>
          <MapContents
            isEmpty={
              [
                ...tasks,
                ...mapImages,
                ...areas,
                ...(zones ? zones : []),
                ...routes
              ].length === 0
            }
            showAllTasks={showAllTasks}
            showTaskTitle={showTaskTitle}
            selectedPanel={selectedPanel}
            selectedAllObjectType={selectedAllObjectType}
            onSelectedPanel={onSelectedPanel}
            onShowAllTasks={toggleShowAllTasks}
            setSelectedPanel={setSelectedPanel}
            onSelectAllAssets={onSelectAllAssets}
            setSelectedRouteId={setSelectedRouteId}
            toggleShowTaskTtile={toggleShowTaskTitle}
            setSelectedMapImageId={setSelectedMapImageId}
            handleTaskMenuSelected={handleTaskMenuSelected}
            handleAreaMenuSelected={handleAreaMenuSelected}
            handleZoneMenuSelected={handleZoneMenuSelected}
            handleRouteMenuSelected={handleRouteMenuSelected}
            toggleActionsRouteDialog={toggleActionsRouteDialog}
            toggleActionsMapImageDialog={toggleActionsMapImageDialog}
            handleMapImagesMenuSelected={handleMapImagesMenuSelected}
          />
        </PanelBarItem>

        <PanelBarItem
          id={'behaviours'}
          expanded={mapConfig.activePanelBar === 'behaviours'}
          title={`${zoneId !== '' ? 'Zone map' : 'Map'} behaviours`}>
          <MapBehaviours
            toggleActionsEventDialogVisible={toggleActionsEventDialogVisible}
          />
        </PanelBarItem>

        {zoneId !== '' && (
          <PanelBarItem
            id={'design'}
            title={'Zone map design'}
            expanded={mapConfig.activePanelBar === 'design'}>
            <div
              className={'d-flex flex-column'}
              style={{
                marginTop: '8px',
                gap: '8px'
              }}>
              <span
                style={{
                  fontSize: '12px'
                }}>
                Choose what players see on the map
              </span>

              <div className={'d-flex align-items-center gap-2'}>
                <span>Back button</span>
                <Switch
                  size={'small'}
                  className={'zone-switch'}
                  onChange={(e) =>
                    handleToggleZoneChange(e, 'isEnableBackButton')
                  }
                  defaultChecked={
                    GetZoneById(gameDocument, zoneId)?.isEnableBackButton ??
                    false
                  }
                />
              </div>
              {mapOptions === 'gis' && (
                <div className={'d-flex align-items-center gap-2'}>
                  <span>Zone outline</span>
                  <Switch
                    size={'small'}
                    className={'zone-switch'}
                    onChange={(e) =>
                      handleToggleZoneChange(
                        e,
                        'isOutlineVisibleWhenPlayerEnter'
                      )
                    }
                    defaultChecked={
                      GetZoneById(gameDocument, zoneId)
                        ?.isOutlineVisibleWhenPlayerEnter ?? false
                    }
                  />
                </div>
              )}
            </div>
          </PanelBarItem>
        )}

        {zoneId !== '' && (
          <PanelBarItem
            id={'details'}
            title={'Zone map details'}
            expanded={
              stateLocation !== null || mapConfig.activePanelBar === 'details'
            }>
            <ZoneMapDetails />
          </PanelBarItem>
        )}
      </PanelBar>

      {dialogVisible && (
        <ResourceWindow
          toggleDialog={toggleDialog}
          onSubmit={handleFilesSelected}
          fileType={['image']}
          imageUrl={currentImageResource ?? ''}
          customFileSize={2097152}
          defaultSelectedCategory={'World & Zone maps'}
        />
      )}

      {/* dialog for events and actions */}
      {actionsEventDialogVisible && (
        <WorldMapEvent
          zoneId={zoneId}
          gameDocument={gameDocument}
          title={'Map Behaviours'}
          onCancel={() => toggleActionsEventDialogVisible()}
          onConfirm={handleEventChange}
        />
      )}

      {actionsEditTaskDialogVisible && (
        <TaskEditorWindow
          toggleDialog={toggleEditTaskDialog}
          onSubmit={handleTaskEntity}
          onClose={toggleEditTaskDialog}
          editorEntity={entityEditorValue!}
          editorResource={resourceEditorValue!}
          editorMode={'basic'}
          showExistingTask={true}
          taskCoordinates={liveTaskCoords}
          onCoordinateSave={(lat, lon) =>
            handleTaskCoordinateSave(selectedTask!.id, lat, lon)
          }
        />
      )}

      {actionsAreaDialogVisible && (
        <AreaEditorWindow
          editorEntity={{
            isNew: false,
            entity: GetAreaById(gameDocument, selectedAreaId)!
          }}
          toggleDialog={toggleActionsAreaDialog}
          onClose={toggleActionsAreaDialog}
          onSubmit={async (editorEntity, resource): Promise<void> => {
            let gameDoc = UpdateAreaAsync(
              state.gameDocument!,
              editorEntity.entity.id,
              editorEntity.entity
            );

            const titleRes = resource.at(0);
            if (titleRes) {
              const { entity } = titleRes;
              gameDoc = (await UpdateResourceAsync(
                gameDoc,
                entity.id,
                entity
              )) as any;

              const areaTitleElement = document.getElementById(
                `labelName-area-${editorEntity.entity.boundary.id}`
              );

              if (areaTitleElement && entity.value !== undefined)
                areaTitleElement.innerHTML = entity.value;
            }

            setState((prev) => UpdateGameDocState(prev, gameDoc));
            toggleActionsAreaDialog();
            setSelectedAreaId('');
          }}
          editorMode={'basic'}
          editorResource={
            selectedAreaTitle
              ? [
                  {
                    isNew: true,
                    entity: selectedAreaTitle
                  }
                ]
              : []
          }
        />
      )}

      {actionsRoutesDialogVisible && (
        <RouteEditorWindow
          editorEntity={
            selectedRouteId === ''
              ? {
                  isNew: true,
                  entity: {} as TaskRoute
                }
              : {
                  isNew: false,
                  entity: routes.find(
                    (x) => x.id === selectedRouteId
                  ) as TaskRoute
                }
          }
          routes={routes ?? []}
          toggleDialog={toggleActionsRouteDialog}
          onClose={toggleActionsRouteDialog}
          onSubmit={async (
            editorEntity: EntityEditor<TaskRoute>,
            titleValue?: string
          ): Promise<void> => {
            if (!state.gameDocument) return;

            let updatedDoc = state.gameDocument;
            if (titleValue !== undefined && titleValue !== '') {
              let titleGuid = editorEntity.entity.titleResId;
              if (!titleGuid) {
                titleGuid = uuid();
                editorEntity.entity.titleResId = titleGuid;
                updatedDoc = await AddResourceAsync(
                  updatedDoc,
                  'Title',
                  '',
                  'text',
                  titleValue,
                  titleGuid
                );
              } else {
                let routeTitleRes = GetResourceById(updatedDoc, titleGuid);
                if (routeTitleRes) {
                  routeTitleRes = { ...routeTitleRes, value: titleValue };
                  updatedDoc = await UpdateResourceAsync(
                    updatedDoc,
                    titleGuid,
                    routeTitleRes
                  );
                } else {
                  updatedDoc = await AddResourceAsync(
                    updatedDoc,
                    'Title',
                    '',
                    'text',
                    titleValue,
                    titleGuid
                  );
                }
              }
              editorEntity.entity.name = titleValue;
            }

            const targetMapId =
              zoneId !== ''
                ? state.gameDocument.rules.worldMap.zones.find(
                    (zone: ZoneAssetId) => zone.zoneAssId === zoneId
                  )?.mapAssId ?? ''
                : state.gameDocument.rules?.worldMap?.mapAssId ?? '';

            if (editorEntity.isNew) {
              editorEntity.entity.id = uuid();
              editorEntity.entity.isVisible = true;
            }
            editorEntity.entity.mapId = targetMapId;

            const finalDoc = UpdateRoutesAsync(updatedDoc, editorEntity.entity);
            setState((prev) => UpdateGameDocState(prev, finalDoc));

            toggleActionsRouteDialog();
            setSelectedPanel('');
            onSelectedPanel && onSelectedPanel('');
          }}
          editorMode={'basic'}
          tasks={tasks}
        />
      )}

      {actionsMapImageDialogVisible && (
        <MapImageWindow
          editorEntity={
            selectedMapImageId === ''
              ? {
                  isNew: true,
                  entity: {} as MapImage
                }
              : {
                  isNew: false,
                  entity: mapImages.find(
                    (x) => x.id === selectedMapImageId
                  ) as MapImage
                }
          }
          mapImages={mapImages}
          editorResource={resourceEditorValue!}
          toggleDialog={toggleActionsMapImageDialog}
          onSubmit={(mapImage: EntityEditor<MapImage>) => {
            getMapEntityAsync().then((responseMap) => {
              let extent: [number, number, number, number];
              let center: [number, number];

              const url = GetResourceValue(
                gameDocument!,
                mapImage.entity.imageResId ?? ''
              );

              const mapContext =
                responseMap?.type === 'illustrated' ? mapIllustration : map;

              center = mapContext.getView().getCenter() as [number, number];

              computeImageScale(mapContext, url).then((scale) => {
                const source = new GeoImage({
                  url,
                  imageCenter: center,
                  imageScale: scale
                });

                extent = source.getExtent() as [number, number, number, number];

                const newData: MapImage = {
                  ...mapImage.entity,
                  id: mapImage.isNew ? uuid() : selectedMapImageId,
                  ...(mapImage.isNew && {
                    mapId: responseMap?.id!,
                    extent,
                    center,
                    scale
                  })
                };

                updateMapImageAsset(newData);
              });

              setSelectedPanel('');
              onSelectedPanel && onSelectedPanel('');
            });
          }}
        />
      )}

      {showDeleteIllustrationConfirm && (
        <YesNoDialog
          title={'Confirm removal'}
          onConfirm={onConfirmDeleteIllustration}
          onClose={() => setShowDeleteIllustrationConfirm(false)}>
          Are you sure you want to remove the image?
        </YesNoDialog>
      )}

      {showDeleteZoneConfirmation && (
        <YesNoDialog
          title={'Confirm removal'}
          onConfirm={onConfirmDeleteZone}
          onClose={() => setShowDeleteZoneConfirmation(false)}>
          Are you sure you want to remove the zone?
        </YesNoDialog>
      )}

      {showDeleteTaskConfirm && (
        <YesNoDialog
          title={'Confirm removal'}
          onConfirm={onConfirmDeleteTask}
          onClose={() => setShowDeleteTaskConfirm(false)}>
          Are you sure you want to remove the task?
        </YesNoDialog>
      )}

      {showDeleteAreaConfirmation && (
        <YesNoDialog
          title={'Confirm removal'}
          onConfirm={onConfirmDeleteArea}
          onClose={() => setShowDeleteAreaConfirmation(false)}>
          Are you sure you want to remove the area?
        </YesNoDialog>
      )}

      {showDeleteRouteConfirmation && (
        <YesNoDialog
          title={'Confirm removal'}
          onConfirm={onConfirmDeleteRoute}
          onClose={toggleRouteDeleteDialog}>
          Are you sure you want to remove the route?
        </YesNoDialog>
      )}

      {showDeleteMapImageConfirmation && (
        <YesNoDialog
          title={'Confirm removal'}
          onConfirm={onConfirmDeleteMapImage}
          onClose={toggleMapImageDeleteDialog}>
          Are you sure you want to remove the image?
        </YesNoDialog>
      )}

      {showPreviewTask && (
        <TaskPreviewDialog
          selectedTask={selectedTask}
          toggle={setShowPreviewTask}
        />
      )}

      {taskPopupId &&
        createPortal(
          <MapPopupMenu
            id={taskPopupId}
            menu={
              tasks.find((task) => task.id === taskPopupId)?.taskContentId
                ? taskMenu
                : taskMenu.filter((e) => e.textMenu !== 'Preview')
            }
            popupOverlay={
              (mapInfo?.type === 'illustrated'
                ? mapIllustration
                : map
              ).getOverlayById('task-menu-popup') as Popup
            }
            onMenuSelected={(menu) => {
              const task = tasks.find((task) => task.id === taskPopupId);
              if (task) handleTaskMenuSelected(task.id, menu);
            }}
          />,
          document.getElementById(`task-menu-popup-${taskPopupId}`)!
        )}

      {vectorPopupId &&
        createPortal(
          <MapPopupMenu
            id={vectorPopupId}
            menu={
              vectorPopupId.includes('image-')
                ? imageMenu.map((menu, idx) => {
                    const selectedImage = mapImages.find(
                      (image) =>
                        image.id === vectorPopupId.replaceAll('image-', '')
                    );
                    if (idx === 1)
                      return {
                        classIcon: !selectedImage?.isLocked
                          ? 'lock'
                          : 'lock_open',
                        textMenu: !selectedImage?.isLocked ? 'Lock' : 'Un-lock'
                      };

                    return menu;
                  })
                : zoneAreaMenu
            }
            popupOverlay={
              (mapInfo?.type === 'illustrated'
                ? mapIllustration
                : map
              ).getOverlayById('vector-menu-popup') as Popup
            }
            onMenuSelected={(menu) => {
              if (vectorPopupId.includes('zone-')) {
                const id = vectorPopupId.replaceAll('zone-', '');
                const zone = (zones ?? []).find(
                  (zone) => zone.boundary.id === id
                );
                if (zone) handleZoneMenuSelected(zone.id, menu);
              }

              if (vectorPopupId.includes('area-')) {
                const id = vectorPopupId.replaceAll('area-', '');
                const area = (areas ?? []).find(
                  (area) => area.boundary.id === id
                );
                if (area) handleAreaMenuSelected(area.id, menu);
              }

              if (vectorPopupId.includes('image-')) {
                const id = vectorPopupId.replaceAll('image-', '');
                handleMapImagesMenuSelected(id, menu);
              }
            }}
          />,
          document.getElementById(`vector-menu-popup-${vectorPopupId}`)!
        )}
    </>
  );
};

export default MapEditor;
